import { useMemo } from "react";

import type { BodyTemperatureDataPoint } from "~/api/bodyTemperature";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface UseBodyTemperatureChartDataProps {
  apiData: BodyTemperatureDataPoint[] | null;
  selectedRange: TimeRange;
}

interface UseBodyTemperatureChartDataReturn {
  filteredData: {
    date: string;
    value: number;
    hour?: number;
    datetime?: string;
  }[];
  chartData: ChartDataPoint[];
}

export const useBodyTemperatureChartData = ({
  apiData,
  selectedRange,
}: UseBodyTemperatureChartDataProps): UseBodyTemperatureChartDataReturn => {
  return useMemo(() => {
    if (!apiData || apiData.length === 0) {
      // Return empty arrays when no data
      return {
        filteredData: [],
        chartData: [],
      };
    }

    let chartData: ChartDataPoint[] = [];

    if (selectedRange === "1d") {
      // For hourly data, use the hour as x value
      chartData = apiData.map((item) => ({
        x: new Date(item.date).getHours(),
        y: Number((item.value / 10).toFixed(2)), // Divide by 10 and format to 2 decimal places
      }));
    } else {
      // For other ranges (week, month, 6m, year), use date as x value
      chartData = apiData.map((item) => {
        // Extract just the date part from the API response
        const dateString = item.date;
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-").map(Number);

        // Create date object (timezone doesn't matter for date-only display)
        const date = new Date(year, month - 1, day);

        return {
          x: date,
          y: Number((item.value / 10).toFixed(2)), // Divide by 10 and format to 2 decimal places
        };
      });
    }

    // Sort chart data by x value to ensure proper line connection
    chartData.sort((a, b) => {
      if (a.x instanceof Date && b.x instanceof Date) {
        return a.x.getTime() - b.x.getTime();
      }
      if (typeof a.x === "number" && typeof b.x === "number") {
        return a.x - b.x;
      }
      return 0;
    });

    // Transform API data to match existing interface
    const transformedData = apiData.map((item) => ({
      date: item.date,
      value: Number((item.value / 10).toFixed(2)), // Divide by 10 and format to 2 decimal places
      hour: selectedRange === "1d" ? new Date(item.date).getHours() : undefined,
      datetime: selectedRange === "1d" ? item.date : undefined,
    }));

    return {
      filteredData: transformedData,
      chartData,
    };
  }, [apiData, selectedRange]);
};
